import { CommonModule } from "@angular/common";
import { RouterModule, Routes } from "@angular/router";
import { TaxReturnComponent } from "./tax-return/tax-return.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { SharedModule } from "../../shared/shared.module";
import { SubcategoryComponent } from "./tax-return/subcategory/subcategory.component";
import { TaxReturnSubcategoryService } from "./tax-return/subcategory/subcategory.service";
import { QuestionGroupCardComponent } from "./tax-return/subcategory/question-group-card/question-group-card.component";
import { CategoryMetaCountPipe } from "./tax-return/category-meta-count.pipe";
import { CommentsComponent } from "./tax-return/subcategory/comments/comments.component";
import { AuthGuard } from "../login/auth-guard/auth.guard";
import { TaxReturnsResolver } from "./tax-returns-resolver";
import { AnswerTruncateDirective } from "./tax-return/subcategory/question-group-card/answer-truncate.directive";
import { DynamicSourcesComponent } from "../dynamic-sources/dynamic-sources.component";
import { DynamicSourcesDropdownComponent } from "../dynamic-sources-dropdown/dynamic-sources-dropdown.component";
import { AnswerTraceComponent } from "./tax-return/subcategory/answer-trace/answer-trace.component";
import { ChangeHistoryComponent } from "./tax-return/change-history/change-history.component";
import { SnapshotBannerComponent } from "./tax-return/snapshot-banner/snapshot-banner.component";
import { MessageBannerComponent } from "./tax-return/message-banner/message-banner.component";
import { MissingInformationComponent } from "./tax-return/missing-information/missing-information.component";
import { TaxReturnNotesComponent } from "./tax-return/tax-return-notes/tax-return-notes.component";
import { QuestionTableRowComponent } from "./tax-return/subcategory/question-group-card/question-table-row/question-table-row.component";
import { NgClickOutsideDelayOutsideDirective, NgClickOutsideDirective, NgClickOutsideExcludeDirective } from "ng-click-outside2";
import { TableColumnToggleComponent } from "./tax-return/subcategory/question-group-card/table-column-toggle/table-column-toggle.component";
import { WarningsComponent } from "./tax-return/warnings/warnings.component";
import { BatchWarningsComponent } from "./tax-return/batch-warnings/batch-warnings.component";
import { BatchOverridesComponent } from "../tax-returns/tax-return/batch-overrides/batch-overrides.component";
import { RubixDocumentsComponent } from "./tax-return/rubix-documents/rubix-documents.component";
import { NavigationModule } from "./tax-return/rubix-documents/navigation/navbar.module";
import { FolderViewModule } from "./tax-return/rubix-documents/folder-view/folder-view.module";
import { RubixDocumentsService } from "./tax-return/rubix-documents/rubix-documents.service";
import { MatTooltipModule } from '@angular/material/tooltip';
import { ModernTableComponent } from "./tax-return/subcategory/question-group-card/modern-table/modern-table.component";
import { ModernTableRowComponent } from "./tax-return/subcategory/question-group-card/modern-table-row/modern-table-row.component";
import { ModernTableViewControlsComponent } from "./tax-return/subcategory/question-group-card/modern-table-view-controls/modern-table-view-controls.component";
import { ChartComponent } from "./tax-return/subcategory/question-group-card/chart/chart.component";
import { provideCharts, withDefaultRegisterables } from "ng2-charts";
import { TenantConfigService } from "../../shared/services/tenant-config.service";
import { NgModule } from "@angular/core";
import { TRANSLOCO_SCOPE, TranslocoModule } from "@jsverse/transloco";
import { SmartFormViewComponent } from "./tax-return/smart-form-view/smart-form-view.component";
import { SmartFormViewTableComponent } from "./tax-return/smart-form-view-table/smart-form-view-table.component";

import { CanDeactivateGuard } from "./tax-return/subcategory/guard.service";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { VirtualScrollerComponent } from "../../shared/virtual-scrolling/virtual-scroller.component";

const taxReturnsRoutes: Routes = [];

TenantConfigService.getTaxReturnAliasPaths().forEach(path => {
  taxReturnsRoutes.push(
    {
      path: `${path}/:id`,
      component: TaxReturnComponent,
      data: {
        routeName: path.slice(0, -1)
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    {
      path: `${path}/:id/subcategories/:subCategoryId`,
      component: SubcategoryComponent,
      data: {
        hideSidebar: true
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      },
      canDeactivate: [CanDeactivateGuard]
    },
    {
      path: `${path}/:id/smart-forms/:smartFormId`,
      component: SmartFormViewComponent,
      data: {
        hideSidebar: true
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    {
      path: `${path}/:id/dynamic-sources`,
      component: DynamicSourcesComponent,
      data: {
        routeName: path.slice(0, -1)
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      },
      canDeactivate: [CanDeactivateGuard]
    },
    {
      path: `${path}/:id/dynamic-sources-dropdown`,
      component: DynamicSourcesDropdownComponent,
      data: {
        routeName: path.slice(0, -1)
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    {
      path: `${path}/:id/change-history`,
      component: ChangeHistoryComponent,
      data: {
        routeName: path.slice(0, -1),
        requestFullData: true
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    {
      path: `${path}/:id/missing-information`,
      component: MissingInformationComponent,
      data: {
        routeName: path.slice(0, -1),
        requestFullData: true
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    {
      path: `${path}/:id/warnings`,
      component: WarningsComponent,
      data: {
        routeName: path.slice(0, -1),
        requestFullData: true
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    {
      path: `${path}/:id/subcategories/:subCategoryId/question/:questionId`,
      component: SubcategoryComponent,
      data: {
        hideSidebar: true
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    {
      path: `${path}/:id/notes`,
      component: TaxReturnNotesComponent,
      data: {
        routeName: path.slice(0, -1)
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    {
      path: `${path}/:id/documents`,
      component: RubixDocumentsComponent,
      data: {
        routeName: path.slice(0, -1)
      },
      canActivate: [AuthGuard],
      resolve: {
        taxReturn: TaxReturnsResolver
      }
    },
    { path: "tax-returns/:id/chartType", component: ChartComponent }
  );
});

@NgModule({
  imports: [
    MatTooltipModule,
    SharedModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule.forRoot(taxReturnsRoutes, {
      onSameUrlNavigation: "reload",
    }),
    NgClickOutsideDirective,
    NgClickOutsideExcludeDirective,
    NgClickOutsideDelayOutsideDirective,
    DragDropModule,
    TranslocoModule,
    NavigationModule,
    FolderViewModule,
    VirtualScrollerComponent,
  ],
  providers: [
    TaxReturnsResolver,
    TaxReturnSubcategoryService,
    RubixDocumentsService,
    {
      provide: TRANSLOCO_SCOPE,
      useValue: { scope: "tax-returns", alias: "taxReturns" }
    },
    CanDeactivateGuard,
    provideCharts(withDefaultRegisterables()),
  ],
  exports: [TaxReturnComponent, CategoryMetaCountPipe],
  declarations: [
    TaxReturnComponent,
    SubcategoryComponent,
    SmartFormViewComponent,
    SmartFormViewTableComponent,
    QuestionGroupCardComponent,
    CategoryMetaCountPipe,
    CommentsComponent,
    AnswerTruncateDirective,
    AnswerTraceComponent,
    ChangeHistoryComponent,
    SnapshotBannerComponent,
    MessageBannerComponent,
    MissingInformationComponent,
    TaxReturnNotesComponent,
    QuestionTableRowComponent,
    TableColumnToggleComponent,
    WarningsComponent,
    BatchWarningsComponent,
    BatchOverridesComponent,
    RubixDocumentsComponent,
    ModernTableComponent,
    ModernTableRowComponent,
    ModernTableViewControlsComponent,
    ChartComponent
  ]
})
export class TaxReturnsModule {}
